import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import {
  ModuleBase,
  Box,
  HeadingTag,
  Text,
  ResponsiveImage,
  YouTubePlayer,
  VimeoPlayer,
  FullBleedModal,
} from '@/components';
import { isColorDark } from '@/utils';

// === LAYOUT CONFIGURATION ===
const getLayoutClasses = (layoutType) => {
  const defaults = {
    container: 'flex-col items-center',
    textWrapper: 'w-full mb-8 lg:mb-12 max-w-4xl text-center',
    videoWrapper: 'w-full',
    textAlign: 'text-center',
    headingAlign: 'text-center',
  };

  switch (layoutType) {
    case 'TextLeft50':
      return {
        container: 'flex-col lg:flex-row lg:items-center lg:gap-10 xl:gap-[100px]',
        textWrapper: 'w-full lg:w-1/2 mb-8 lg:mb-0 text-left',
        videoWrapper: 'w-full lg:w-1/2',
        textAlign: 'text-left',
        headingAlign: 'text-left',
      };
    case 'TextRight50':
      return {
        container: 'flex-col lg:flex-row-reverse lg:items-center lg:gap-10 xl:gap-[100px]',
        textWrapper: 'w-full lg:w-1/2 mb-8 lg:mb-0 text-left',
        videoWrapper: 'w-full lg:w-1/2',
        textAlign: 'text-left',
        headingAlign: 'text-left',
      };
    case 'TextLeft33':
      return {
        container: 'flex-col lg:flex-row lg:items-center lg:gap-10 xl:gap-[100px]',
        textWrapper: 'w-full lg:w-1/3 mb-8 lg:mb-0 text-left',
        videoWrapper: 'w-full lg:w-2/3',
        textAlign: 'text-left',
        headingAlign: 'text-left',
      };
    case 'TextRight33':
      return {
        container: 'flex-col lg:flex-row-reverse lg:items-center lg:gap-10 xl:gap-[100px]',
        textWrapper: 'w-full lg:w-1/3 mb-8 lg:mb-0 text-left',
        videoWrapper: 'w-full lg:w-2/3',
        textAlign: 'text-left',
        headingAlign: 'text-left',
      };
    case 'Stacked':
    default:
      return defaults;
  }
};

const getAspectRatioClass = (ratio) => {
  switch (ratio) {
    case '16:9':
      return 'aspect-video';
    case '4:3':
      return 'aspect-[4/3]';
    case '3:2':
      return 'aspect-[3/2]';
    case '1:1':
    case 'Square':
      return 'aspect-square';
    case '2:1':
    default:
      return 'aspect-[2/1]';
  }
};

// === PLAY BUTTON ICON COMPONENT ===
const PlayButtonOverlay = ({ onClick }) => (
  <button
    onClick={onClick}
    className="group absolute inset-0 z-20 flex size-full cursor-pointer items-center justify-center bg-black/20 transition-colors hover:bg-black/30"
    aria-label="Play Video"
  >
    <div className="flex size-16 items-center justify-center rounded-full bg-white/50 shadow-lg transition-transform duration-300 group-hover:scale-110 sm:size-20">
      <svg className="ml-1 size-6 fill-black sm:size-8" viewBox="0 0 24 24">
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  </button>
);

const VideoModule = ({ data }) => {
  const router = useRouter();
  const containerRef = useRef();

  // Unique ID for this specific module instance to avoid pausing itself via global events
  const moduleId = useRef(`video-module-${Math.random().toString(36).substr(2, 9)}`);

  const inlineYoutubeRef = useRef();
  const inlineVimeoRef = useRef();
  const modalRef = useRef();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // 1. Destructure
  const {
    headingTitle,
    description,
    videoType = {},
    aspectRatio = '2:1',
    backgroundColour,
    anchor,
    layout = 'Stacked',
  } = data || {};

  const {
    coverImage: videoCoverImage,
    youtubeId,
    vimeoId,
    mobileVimeoId,
    videoFromGallery: localVideoUrl,
    autoPlay: videoAutoPlay,
    loop: videoLoop,
    allowFullScreen: videoAllowFullScreen,
    hideAudioControls: videoHideAudioControls,
  } = videoType;

  const isYouTube = !!youtubeId;
  const isVimeo = !!vimeoId;
  const isLocalVideo = !!localVideoUrl;
  const isDark = isColorDark(backgroundColour);
  const styles = getLayoutClasses(layout);
  const aspectRatioClass = getAspectRatioClass(aspectRatio);

  // Logic to safely check if text content exists
  const hasHeading = headingTitle?.heading && headingTitle.heading.trim().length > 0;
  const hasDescription = description && description.trim().length > 0;

  // Only render the text wrapper if actual content exists
  const shouldRenderText = hasHeading || hasDescription;

  // Show custom thumbnail ONLY if autoPlay is OFF, user hasn't clicked, AND there's a cover image
  const showCustomThumbnail = !videoAutoPlay && !hasInteracted && !!videoCoverImage;

  // === PLAYER HELPERS ===
  const pauseInlinePlayer = () => {
    setIsPlaying(false);

    // Pause YouTube
    const yt = inlineYoutubeRef.current?.internalPlayer || inlineYoutubeRef.current;
    if (yt?.pauseVideo) yt.pauseVideo();

    // Pause Vimeo
    const vimeo = inlineVimeoRef.current;
    if (vimeo) {
      // Handle various vimeo wrapper structures
      const p = vimeo.play ? vimeo : vimeo.player || vimeo.internalPlayer;
      if (p?.pause) p.pause();
    }
  };

  const playInlinePlayer = () => {
    setIsPlaying(true);
    // Dispatch global pause event so other modules stop
    dispatchGlobalPause();

    // Play YouTube
    const yt = inlineYoutubeRef.current?.internalPlayer || inlineYoutubeRef.current;
    if (yt?.playVideo) yt.playVideo();

    // Play Vimeo
    const vimeo = inlineVimeoRef.current;
    if (vimeo) {
      const p = vimeo.play ? vimeo : vimeo.player || vimeo.internalPlayer;
      if (p?.play) p.play();
    }
  };

  const dispatchGlobalPause = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('eleven:pause-all-videos', { detail: { sender: moduleId.current } }));
    }
  };

  // === GLOBAL EVENT LISTENER ===
  // Listen for "pause all" events from other modules
  useEffect(() => {
    const handleGlobalPause = (e) => {
      // If the event came from ANOTHER module, pause my inline player
      if (e.detail?.sender !== moduleId.current) {
        pauseInlinePlayer();
      }
    };

    window.addEventListener('eleven:pause-all-videos', handleGlobalPause);
    return () => window.removeEventListener('eleven:pause-all-videos', handleGlobalPause);
  }, []);

  // === HASH / FULLSCREEN LOGIC ===
  useEffect(() => {
    if (!router.isReady || !anchor) return;
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === `${anchor}-fullscreen`) {
        containerRef.current?.scrollIntoView({ behavior: 'auto', block: 'center' });

        // 1. Pause the inline player immediately (if it was autoplaying)
        pauseInlinePlayer();

        // 2. Stop other modules
        dispatchGlobalPause();

        setTimeout(() => {
          // FIX: Do NOT set hasInteracted(true). This prevents the inline player
          // from mistakenly thinking it should start playing when the modal opens.
          setIsModalOpen(true);
          modalRef.current?.open();
        }, 100);
      } else if (hash === anchor) {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [router.isReady, anchor]);

  const handleCloseModal = () => {
    if (modalRef.current) modalRef.current.close();
    setIsModalOpen(false);
    if (anchor && typeof window !== 'undefined') window.history.replaceState(null, null, `#${anchor}`);
  };

  // === HANDLERS ===
  const handleThumbnailClick = () => {
    setHasInteracted(true);
    // Use timeout to allow state update to process, then play
    setTimeout(() => {
      playInlinePlayer();
    }, 25);
  };

  const handleInvisibleClick = () => {
    if (isPlaying) {
      pauseInlinePlayer();
    } else {
      playInlinePlayer();
    }
  };

  const commonMediaProps = {
    loop: videoLoop,
    controls: !videoHideAudioControls,
    muted: videoHideAudioControls,
  };

  // Determine effective autoPlay: backend setting OR user clicked thumbnail
  const effectiveAutoPlay = videoAutoPlay || hasInteracted;

  const inlineMediaObject = isYouTube
    ? { youtubeId, autoPlay: effectiveAutoPlay, ...commonMediaProps }
    : isVimeo
      ? { vimeoId, mobileVimeoId, autoPlay: effectiveAutoPlay, ...commonMediaProps }
      : null;

  const modalMediaObject = isYouTube
    ? { youtubeId, autoPlay: true, ...commonMediaProps }
    : isVimeo
      ? { vimeoId, mobileVimeoId, autoPlay: true, ...commonMediaProps }
      : null;

  if (!data) return null;
  if (!youtubeId && !vimeoId && !localVideoUrl) return null;

  return (
    <ModuleBase data={data} ref={containerRef}>
      <div className={classNames('container flex px-6 md:px-10 xl:px-[156px]', styles.container)}>
        {/* Text Wrapper - Render Only if content exists */}
        {shouldRenderText && (
          <div className={classNames('text-wrapper', styles.textWrapper)}>
            {hasHeading && (
              <HeadingTag
                animate
                data={headingTitle}
                className={classNames(
                  'mb-4 font-larken text-2xl font-light leading-7 md:text-desktop-medium md:leading-desktop-medium lg:mb-6',
                  styles.headingAlign,
                  isDark ? 'text-white' : 'text-black',
                )}
              />
            )}
            {hasDescription && (
              <Text
                dangerouslySetInnerHTML={{ __html: description }}
                className={classNames(
                  'font-gotham text-sm font-s-light leading-6 [&_p]:opacity-90',
                  styles.textAlign,
                  isDark ? 'text-white/75' : 'text-black/75',
                )}
              />
            )}
          </div>
        )}

        {/* Video Wrapper */}
        <div className={classNames('video-wrapper', styles.videoWrapper)}>
          <Box className={classNames('relative min-h-[250px] w-full overflow-hidden', aspectRatioClass)}>
            <div className="relative size-full">
              {/* LAYER 1: Custom Thumbnail Overlay */}
              {showCustomThumbnail && (
                <div className="absolute inset-0 z-30 size-full">
                  <ResponsiveImage
                    image={videoCoverImage}
                    widths={{ xs: 345, sm: 768, md: 1024, lg: 1200, xl: 1600 }}
                    heights={{ xs: 173, sm: 384, md: 512, lg: 600, xl: 800 }}
                    className="size-full object-cover"
                  />
                  <PlayButtonOverlay onClick={handleThumbnailClick} />
                </div>
              )}

              {/* LAYER 2: Invisible Click Area */}
              {!showCustomThumbnail && videoHideAudioControls && (
                <div
                  className="absolute inset-0 z-20 cursor-pointer"
                  onClick={handleInvisibleClick}
                  title={isPlaying ? 'Click to Pause' : 'Click to Play'}
                />
              )}

              {/* LAYER 3: The Players */}
              <div className="relative z-10 size-full">
                {isYouTube && (
                  <YouTubePlayer
                    ref={inlineYoutubeRef}
                    media={inlineMediaObject}
                    cover={videoCoverImage}
                    className="size-full"
                    allowFullScreen={true}
                    {...commonMediaProps}
                  />
                )}

                {isVimeo && (
                  <VimeoPlayer
                    ref={inlineVimeoRef}
                    media={inlineMediaObject}
                    cover={videoCoverImage}
                    background={false}
                    hideVideoUntilPlay={false}
                    className="absolute inset-0 size-full [&_iframe]:!h-full [&_iframe]:!w-full"
                    {...commonMediaProps}
                  />
                )}

                {isLocalVideo && (
                  <video
                    src={localVideoUrl}
                    poster={videoCoverImage?.ImageUrl}
                    autoPlay={effectiveAutoPlay}
                    playsInline
                    className="size-full object-cover"
                    loop={videoLoop}
                    controls={!videoHideAudioControls}
                    muted={videoHideAudioControls}
                    controlsList={!videoAllowFullScreen ? 'nofullscreen' : undefined}
                  />
                )}
              </div>
            </div>
          </Box>
        </div>
      </div>

      {/* Modal */}
      <FullBleedModal ref={modalRef} onClose={handleCloseModal}>
        <div
          className="relative flex size-full cursor-pointer items-center justify-center p-6"
          onClick={handleCloseModal}
        >
          {isModalOpen && (
            <div
              className="flex h-full max-h-[90vh] w-full max-w-[90vw] cursor-default items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {isYouTube && (
                <YouTubePlayer media={modalMediaObject} className="size-full" controls={true} muted={false} />
              )}
              {isVimeo && (
                <VimeoPlayer
                  media={modalMediaObject}
                  background={false}
                  className="absolute inset-0 size-full [&_iframe]:!h-full [&_iframe]:!w-full"
                  controls={true}
                  muted={false}
                  allowFullScreen={true}
                  hideVideoUntilPlay={false}
                />
              )}
              {isLocalVideo && <video src={localVideoUrl} autoPlay controls className="max-h-full max-w-full" />}
            </div>
          )}
        </div>
      </FullBleedModal>
    </ModuleBase>
  );
};

VideoModule.propTypes = {
  data: PropTypes.shape({
    layout: PropTypes.string,
    headingTitle: PropTypes.shape({
      heading: PropTypes.string,
      htag: PropTypes.string,
    }),
    description: PropTypes.string,
    videoType: PropTypes.shape({
      coverImage: PropTypes.object,
      youtubeId: PropTypes.string,
      vimeoId: PropTypes.string,
      mobileVimeoId: PropTypes.string,
      videoFromGallery: PropTypes.string,
      autoPlay: PropTypes.bool,
      loop: PropTypes.bool,
      allowFullScreen: PropTypes.bool,
      hideAudioControls: PropTypes.bool,
    }),
    aspectRatio: PropTypes.oneOf(['2:1', '16:9', '4:3', '3:2', '1:1', 'Square']),
    backgroundColour: PropTypes.string,
    anchor: PropTypes.string,
    moduleName: PropTypes.string,
  }).isRequired,
};

export default VideoModule;
